/**
 * Missing-in-SCH projects service (Story SCH-5.6).
 *
 * Wraps two endpoints:
 *   - GET    /missing-projects                        (admin / superadmin)
 *   - PATCH  /missing-projects/{id}/decision          (admin / superadmin)
 */

import { supabase } from '@/lib/supabaseClient';
import type {
  MissingProject,
  MissingProjectDecision,
  MissingProjectsErrorObject,
  MissingProjectsResponse,
  MissingProjectsResult,
} from '@/types/integrations/missingProjects';

const BACKEND_URL =
  import.meta.env.VITE_PYTHON_BACKEND_URL || 'http://localhost:8000';

async function authHeader(): Promise<HeadersInit> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error('Not authenticated');
  }
  return {
    Authorization: `Bearer ${session.access_token}`,
    'Content-Type': 'application/json',
  };
}

interface ApiEnvelope<TData> {
  data: TData | null;
  error: { code: string; message: string; details?: Record<string, unknown> } | null;
  meta: unknown;
}

async function fetchEnvelope<TData>(
  path: string,
  init: RequestInit,
): Promise<ApiEnvelope<TData> | { _http_403: true }> {
  const headers = await authHeader();
  const response = await fetch(`${BACKEND_URL}${path}`, {
    ...init,
    headers: { ...headers, ...(init.headers ?? {}) },
  });
  if (response.status === 403) {
    return { _http_403: true };
  }
  if (!response.ok && response.status !== 200) {
    let detail: string | undefined;
    try {
      const json = await response.json();
      detail = typeof json?.detail === 'string' ? json.detail : undefined;
    } catch {
      // ignore
    }
    throw new Error(detail || `Request failed with status ${response.status}`);
  }
  return (await response.json()) as ApiEnvelope<TData>;
}

const KNOWN = ['forbidden', 'not_found', 'bad_request', 'internal_error'] as const;

function _normalizeError(
  raw: { code: string; message: string; details?: Record<string, unknown> } | null,
): MissingProjectsErrorObject {
  if (!raw) return { code: 'internal_error', message: 'Unknown error' };
  const code = (KNOWN as readonly string[]).includes(raw.code)
    ? (raw.code as MissingProjectsErrorObject['code'])
    : 'internal_error';
  return { code, message: raw.message, details: raw.details };
}

interface RawMissingProject {
  id: string;
  external_id: string | null;
  project_name: string | null;
  first_name: string | null;
  last_name: string | null;
  current_stage_id: number | null;
  missing_since_at: string | null;
}

function _mapRow(raw: RawMissingProject): MissingProject {
  return {
    id: raw.id,
    externalId: raw.external_id ?? null,
    projectName: raw.project_name ?? null,
    firstName: raw.first_name ?? null,
    lastName: raw.last_name ?? null,
    currentStageId: raw.current_stage_id ?? null,
    missingSinceAt: raw.missing_since_at ?? null,
  };
}

async function listMissingProjects(): Promise<
  MissingProjectsResult<MissingProjectsResponse>
> {
  const result = await fetchEnvelope<{
    data: RawMissingProject[];
    total_count: number;
  }>('/api/v1/integrations/subcontractorhub/missing-projects', { method: 'GET' });

  if ('_http_403' in result) {
    return {
      data: null,
      error: {
        code: 'forbidden',
        message:
          'Administrator access is required to view missing projects.',
      },
    };
  }
  if (result.error) return { data: null, error: _normalizeError(result.error) };
  if (!result.data) {
    return {
      data: null,
      error: { code: 'internal_error', message: 'Empty response' },
    };
  }
  return {
    data: {
      data: (result.data.data ?? []).map(_mapRow),
      totalCount: result.data.total_count ?? 0,
    },
    error: null,
  };
}

async function decideMissingProject(
  projectId: string,
  decision: MissingProjectDecision,
): Promise<MissingProjectsResult<{ projectId: string; decision: MissingProjectDecision }>> {
  const result = await fetchEnvelope<{ project_id: string; decision: MissingProjectDecision }>(
    `/api/v1/integrations/subcontractorhub/missing-projects/${encodeURIComponent(projectId)}/decision`,
    {
      method: 'PATCH',
      body: JSON.stringify({ decision }),
    },
  );
  if ('_http_403' in result) {
    return { data: null, error: { code: 'forbidden', message: 'Forbidden' } };
  }
  if (result.error) return { data: null, error: _normalizeError(result.error) };
  if (!result.data) {
    return {
      data: null,
      error: { code: 'internal_error', message: 'Empty response' },
    };
  }
  return {
    data: { projectId: result.data.project_id, decision: result.data.decision },
    error: null,
  };
}

export const missingProjectsService = {
  listMissingProjects,
  decideMissingProject,
};
